import React, { useState, useEffect } from 'react';
import { getTenantBanks, addTenantBank, toggleTenantBank, deleteTenantBank, updateTenantBanks } from '../api/client';

const TYPE_LABELS = {
  pos: 'Punto de Venta (POS)',
  efectivo: 'Efectivo',
  transferencia: 'Transferencia / Zelle',
  digital: 'Pago Digital',
  pago_movil: 'Pago Móvil',
  otro: 'Otro',
};

const TYPE_COLORS = {
  pos: 'bg-blue-100 text-blue-800',
  efectivo: 'bg-green-100 text-green-800',
  transferencia: 'bg-purple-100 text-purple-800',
  digital: 'bg-yellow-100 text-yellow-800',
  pago_movil: 'bg-orange-100 text-orange-800',
  otro: 'bg-gray-100 text-gray-800',
};

const DEFAULT_KEYS = new Set([
  'pos_banesco','pos_bdv','pos_bnc','pos_banplus','pos_provincial',
  'cash_usd','cash_ves','zelle_transfer','biopago','cashea',
  'pago_movil_banesco','pago_movil_bdv','pago_movil_bnc','pago_movil_banplus',
  'credito','devolucion',
]);

const emptyForm = { label: '', key: '', currency: 'VES', type: 'pos' };

export default function BankSettings() {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await getTenantBanks();
      setBanks(data);
    } catch {
      setError('Error cargando bancos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (key) => {
    try {
      const res = await toggleTenantBank(key);
      setBanks(res.banks);
    } catch {
      setError('Error actualizando banco');
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm('¿Eliminar este método de pago?')) return;
    try {
      const res = await deleteTenantBank(key);
      setBanks(res.banks);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error eliminando banco');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    // Auto-generate key from label if not set
    const key = form.key || form.label.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    try {
      const res = await addTenantBank({ ...form, key });
      setBanks(res.banks);
      setShowForm(false);
      setForm(emptyForm);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error agregando banco');
    } finally {
      setSaving(false);
    }
  };

  // Group by type
  const byType = banks.reduce((acc, b) => {
    const t = b.type || 'otro';
    if (!acc[t]) acc[t] = [];
    acc[t].push(b);
    return acc;
  }, {});

  const enabledCount = banks.filter(b => b.enabled).length;

  if (loading) return <div className="p-8 text-center text-gray-500">Cargando configuración de bancos...</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Métodos de Pago</h1>
          <p className="text-gray-500 mt-1">{enabledCount} de {banks.length} activos · Se muestran en el formulario de ventas</p>
        </div>
        <button onClick={() => { setShowForm(true); setError(''); }} className="btn-primary">
          + Agregar Banco
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Add bank form */}
      {showForm && (
        <div className="card mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">Nuevo método de pago</h3>
          <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Nombre visible *</label>
              <input
                className="form-input"
                placeholder="ej: POS BFC"
                value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="form-label">Clave interna (opcional)</label>
              <input
                className="form-input"
                placeholder="ej: pos_bfc (auto si vacío)"
                value={form.key}
                onChange={e => setForm(f => ({ ...f, key: e.target.value.toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,'') }))}
              />
            </div>
            <div>
              <label className="form-label">Tipo</label>
              <select className="form-input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                {Object.entries(TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Moneda</label>
              <select className="form-input" value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                <option value="VES">Bolívares (Bs.)</option>
                <option value="USD">Dólares (USD)</option>
              </select>
            </div>
            <div className="md:col-span-2 flex gap-3">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => { setShowForm(false); setForm(emptyForm); }}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Banks grouped by type */}
      {Object.entries(byType).map(([type, items]) => (
        <div key={type} className="card mb-4">
          <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded-full ${TYPE_COLORS[type] || 'bg-gray-100 text-gray-800'}`}>
              {TYPE_LABELS[type] || type}
            </span>
          </h3>
          <div className="divide-y divide-gray-100">
            {items.map(bank => (
              <div key={bank.key} className={`flex items-center justify-between py-3 ${!bank.enabled ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-3">
                  <div>
                    <span className="font-medium text-gray-900">{bank.label}</span>
                    <span className="ml-2 text-xs text-gray-400">{bank.key}</span>
                    <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${bank.currency === 'USD' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                      {bank.currency}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <div
                      onClick={() => handleToggle(bank.key)}
                      className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${bank.enabled ? 'bg-blue-500' : 'bg-gray-300'}`}
                    >
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${bank.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </div>
                    <span className="text-sm text-gray-600">{bank.enabled ? 'Activo' : 'Inactivo'}</span>
                  </label>
                  {!DEFAULT_KEYS.has(bank.key) && (
                    <button
                      onClick={() => handleDelete(bank.key)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
